import { phMedia, type PHMedia } from "../../shared/planetHoppers";
import { t } from "./i18n";
import { phEscape as escape } from "./planetHoppersView";
import { deduplicatePHImages, searchPHImages, type PHImageResult, type PHImageSearchCursor, type PHImageSource, type PHImageSourceFilter } from "./planetImageSearch";

const sourceName = (source: PHImageSource) => t(source === "openverse" ? "phImagesOpenverse" : source === "commons" ? "phImagesCommons" : "phImagesWikipedia");

export function phMediaPickerMarkup(slot: "image" | "after"): string {
    return `
        <form class="ph-image-search-bar" data-image-search-form>
            <input type="search" data-query aria-label="${escape(t("phSearchImages"))}" placeholder="${escape(t("phSearchImages"))}" autocomplete="off"/>
            <select data-image-source aria-label="${escape(t("phImageSource"))}">
                <option value="all">${escape(t("phImagesAllSources"))}</option>
                ${(["openverse", "commons", "wikipedia"] as const).map(source => `<option value="${source}">${escape(sourceName(source))}</option>`).join("")}
            </select>
            <button type="submit" data-image-search>${escape(t("phImageSearch"))}</button>
        </form>
        <p class="ph-media-search-status" data-media-error role="status" aria-live="polite"></p>
        <div class="ph-search-results"></div>
        <button class="ph-image-more" data-image-more hidden>${escape(t("phMoreImages"))}</button>
        <div class="ph-media-url-row">
            <input type="url" data-media-url aria-label="${escape(t(slot === "image" ? "phImageUrl" : "phImageVideoUrl"))}" placeholder="${escape(t(slot === "image" ? "phImageUrl" : "phImageVideoUrl"))}"/>
            <button type="button" data-url-add>${escape(t("phAddMediaLink"))}</button>
            <label class="secondary-button">${escape(t("phUploadImage"))}<input type="file" data-media-file accept="image/png,image/jpeg,image/webp,image/gif" hidden/></label>
        </div>
        <div class="ph-video-clip" data-video-clip hidden>
            <span>${escape(t("phVideoClip"))}</span>
            <label>${escape(t("phVideoStart"))}<input type="number" data-video-start min="0" max="86400" value="0"/></label>
            <label>${escape(t("phVideoEnd"))}<input type="number" data-video-end min="0" max="86400"/></label>
        </div>`;
}

export function mountPHMediaPicker(dialog: HTMLDialogElement, slot: "image" | "after", actions: {
    attach: (media: PHMedia) => void;
    upload: (file: Blob, credit?: string) => Promise<boolean>;
}): void {
    const query = dialog.querySelector<HTMLInputElement>("[data-query]")!;
    const source = dialog.querySelector<HTMLSelectElement>("[data-image-source]")!;
    const searchButton = dialog.querySelector<HTMLButtonElement>("[data-image-search]")!;
    const more = dialog.querySelector<HTMLButtonElement>("[data-image-more]")!;
    const status = dialog.querySelector<HTMLElement>("[data-media-error]")!;
    const results = dialog.querySelector<HTMLElement>(".ph-search-results")!;
    const url = dialog.querySelector<HTMLInputElement>("[data-media-url]")!;
    const clip = dialog.querySelector<HTMLElement>("[data-video-clip]")!;
    const start = dialog.querySelector<HTMLInputElement>("[data-video-start]")!;
    const end = dialog.querySelector<HTMLInputElement>("[data-video-end]")!;
    let images: PHImageResult[] = [];
    let cursor: PHImageSearchCursor | undefined;
    let controller: AbortController | null = null;
    let searchedQuery = "";
    let searchedSource: PHImageSourceFilter = "all";
    let attaching = false;

    const renderResults = () => {
        results.innerHTML = images.map((image, index) => `<button type="button" class="ph-image-result" data-image-result="${index}" title="${escape(image.title)}" aria-label="${escape(t("phUseImage", { title: image.title }))}" ${attaching ? "disabled" : ""}>
            <img src="${escape(image.thumbnail)}" alt="${escape(image.title)}" loading="lazy" referrerpolicy="no-referrer"/>
            <span class="ph-image-result-title">${escape(image.title)}</span>
            <span class="ph-image-result-source">${escape(sourceName(image.source))}</span>
        </button>`).join("");
    };
    const search = async (append = false) => {
        const text = query.value.trim();
        if (!text || attaching) return;
        controller?.abort();
        const request = controller = new AbortController();
        if (!append) { images = []; cursor = undefined; searchedQuery = text; searchedSource = source.value as PHImageSourceFilter; renderResults(); }
        searchButton.disabled = true; more.disabled = true;
        status.textContent = t("phImageSearching");
        try {
            const page = await searchPHImages(searchedQuery, searchedSource, append ? cursor : undefined, request.signal);
            if (!dialog.open || request !== controller || text !== query.value.trim()) return;
            images = deduplicatePHImages([...images, ...page.images]); cursor = page.next;
            renderResults(); more.hidden = Object.keys(cursor).length === 0;
            status.textContent = page.unavailable.length
                ? t("phImageSourcesUnavailable", { sources: page.unavailable.map(sourceName).join(", ") })
                : images.length ? t("phImageResultCount", { count: images.length }) : t("phNoImages");
        } catch {
            if (request.signal.aborted || !dialog.open) return;
            more.hidden = true;
            status.textContent = t("phImageSearchUnavailable");
        } finally {
            if (request === controller) { searchButton.disabled = false; more.disabled = false; }
        }
    };
    dialog.querySelector("[data-image-search-form]")!.addEventListener("submit", event => { event.preventDefault(); void search(); });
    more.addEventListener("click", () => void search(true));
    source.addEventListener("change", () => { if (searchedQuery) void search(); });
    query.addEventListener("input", () => { controller?.abort(); more.hidden = true; searchButton.disabled = false; });
    dialog.addEventListener("close", () => controller?.abort(), { once: true });

    const upload = async (file: Blob, credit = "") => {
        if (attaching) return;
        attaching = true; renderResults(); status.textContent = t("phImageAttaching");
        try {
            if (await actions.upload(file, credit)) { if (dialog.open) dialog.close(); }
            else status.textContent = t("phImageAttachFailed");
        } catch { status.textContent = t("phImageAttachFailed"); }
        finally { attaching = false; if (dialog.open) renderResults(); }
    };
    results.addEventListener("click", event => {
        const button = (event.target as Element).closest<HTMLButtonElement>("[data-image-result]");
        const image = button ? images[Number(button.dataset.imageResult)] : undefined;
        if (!image || attaching) return;
        attaching = true; renderResults(); status.textContent = t("phImageAttaching");
        void (async () => {
            try {
                const response = await fetch(image.url, { signal: AbortSignal.timeout(15000), referrerPolicy: "no-referrer" });
                if (!response.ok) throw new Error("Image download failed");
                const file = await response.blob();
                if (!file.type.startsWith("image/")) throw new Error("Image download returned non-image data");
                attaching = false;
                if (dialog.open) await upload(file, image.credit);
            } catch { attaching = false; if (dialog.open) { status.textContent = t("phImageAttachFailed"); renderResults(); } }
        })();
    });
    const updateClip = () => {
        const media = phMedia(url.value);
        clip.hidden = slot !== "after" || media?.kind !== "youtube";
        if (!clip.hidden) start.value = String(media?.start || 0);
    };
    url.addEventListener("input", updateClip);
    dialog.querySelector("[data-url-add]")!.addEventListener("click", () => {
        const media = phMedia(url.value);
        if (!media || slot === "image" && media.kind !== "image") { status.textContent = t(slot === "image" ? "phInvalidImageUrl" : "phInvalidMediaUrl"); return; }
        if (media.kind === "youtube") {
            const startValue = Number(start.value);
            const endValue = end.value.trim() ? Number(end.value) : undefined;
            if (!Number.isFinite(startValue) || startValue < 0 || startValue > 86400 || endValue !== undefined && (!Number.isFinite(endValue) || endValue <= startValue || endValue > 86400)) { status.textContent = t("phInvalidVideoTimes"); return; }
            media.start = startValue; media.end = endValue;
        }
        actions.attach(media); dialog.close();
    });
    dialog.querySelector<HTMLInputElement>("[data-media-file]")!.addEventListener("change", event => {
        const file = (event.target as HTMLInputElement).files?.[0]; if (file) void upload(file);
    });
    dialog.addEventListener("paste", event => {
        const file = Array.from(event.clipboardData?.files || []).find(file => file.type.startsWith("image/"));
        if (file) { event.preventDefault(); void upload(file); }
    });
}
